import { Suspense, lazy } from "react";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ThemeProvider } from "@/components/ThemeProvider";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { DemoRequestProvider } from "@/components/DemoRequestProvider";
import HashScrollHandler from "@/components/HashScrollHandler";
import ScrollToTop from "@/components/ScrollToTop";
import SkipToContent from "@/components/SkipToContent";
import AnnouncementBar from "@/components/AnnouncementBar";
import CookieConsent from "./components/CookieConsent";
import Index from "./pages/Index";

// Lazy-load toasters — they only appear after user interaction, so keep
// sonner + radix-toast + floating-ui out of the critical bundle.
const Toaster = lazy(() =>
  import("@/components/ui/toaster").then((m) => ({ default: m.Toaster })),
);
const Sonner = lazy(() =>
  import("@/components/ui/sonner").then((m) => ({ default: m.Toaster })),
);

// Lazy-load secondary routes — keeps the home bundle small.
const SubPage = lazy(() => import("./pages/SubPage"));
const CaseStudies = lazy(() => import("./pages/CaseStudies"));
const StyleGuide = lazy(() => import("./pages/StyleGuide"));
const AuthPage = lazy(() => import("./pages/Auth"));
const SOC2Dashboard = lazy(() => import("./pages/admin/SOC2Dashboard"));
const FondeaWaitlistAdmin = lazy(() => import("./pages/admin/FondeaWaitlistAdmin"));
const AdminRoute = lazy(() => import("./components/admin/AdminRoute"));
const Legal = lazy(() => import("./pages/Legal"));
// Fondea waitlist landing; previous versions kept at ./pages/LanzamientoFondea and ./pages/FondeaComingSoon
const LanzamientoFondea = lazy(() => import("./pages/FondeaWaitlist"));
const SolicitudIniciar = lazy(() => import("./pages/SolicitudIniciar"));
const FondeaProduct = lazy(() => import("./pages/products/FondeaProduct"));
const WhiteLabelProduct = lazy(() => import("./pages/products/WhiteLabelProduct"));
const SolutionPage = lazy(() => import("./pages/solutions/SolutionPage"));
const Contact = lazy(() => import("./pages/Contact"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

const RouteFallback = () => (
  <div className="min-h-screen bg-background" aria-hidden="true" />
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider attribute="class" defaultTheme="dark" enableSystem={false} forcedTheme={undefined}>
      <LanguageProvider>
        <TooltipProvider>
          <Suspense fallback={null}>
            <Toaster />
            <Sonner />
          </Suspense>
          <BrowserRouter>
            <DemoRequestProvider>
              <SkipToContent />
              <AnnouncementBar />
              <ScrollToTop />
              <HashScrollHandler />
              <Suspense fallback={<RouteFallback />}>
                <Routes>
                  <Route path="/" element={<Index />} />
                  <Route path="/fondea" element={<FondeaProduct />} />
                  <Route path="/white-label" element={<WhiteLabelProduct />} />
                  <Route path="/solutions/factoring" element={<SolutionPage slug="factoring" />} />
                  <Route path="/solutions/confirming" element={<SolutionPage slug="confirming" />} />
                  <Route path="/solutions/payments" element={<SolutionPage slug="payments" />} />
                  <Route path="/solutions/auctions" element={<SolutionPage slug="auctions" />} />
                  <Route path="/solutions/integrations" element={<SolutionPage slug="integrations" />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/auth" element={<AuthPage />} />
                  <Route path="/login" element={<AuthPage />} />
                  <Route path="/lanzamiento-fondea" element={<LanzamientoFondea />} />
                  <Route path="/solicitud/iniciar" element={<SolicitudIniciar />} />
                  <Route path="/admin/soc2" element={<AdminRoute><SOC2Dashboard /></AdminRoute>} />
                  <Route path="/admin/lista-espera" element={<AdminRoute><FondeaWaitlistAdmin /></AdminRoute>} />
                  <Route path="/style-guide" element={<StyleGuide />} />
                  <Route path="/resources/case-studies" element={<CaseStudies />} />
                  <Route path="/legal/:slug" element={<Legal />} />
                  <Route path="/:category/:slug" element={<SubPage />} />
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
              <CookieConsent />
            </DemoRequestProvider>
          </BrowserRouter>
        </TooltipProvider>
      </LanguageProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
